import React, { FC, useEffect, useState, useContext } from 'react';

import { Redirect } from 'react-router-dom';

import TokenManagerContext from 'core/auth/TokenManager';

import envs from 'config/envs';

import { ApolloClient, ApolloProvider, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import jwt from 'jsonwebtoken';

interface ITokenValidationProps {}

enum TokenExpiredEnum {
  CHECKING,
  EXPIRED,
  NOTEXPIRED,
  UNAUTHORIZED,
}

const TokenValidation: FC<ITokenValidationProps> = ({ children }) => {
  const manager = useContext(TokenManagerContext);

  const [isValid, setIsValid] = useState<TokenExpiredEnum>(
    TokenExpiredEnum.CHECKING
  );

  const [graphqlClient, setGraphqlClient] =
    useState<ApolloClient<any> | undefined>();

  useEffect(() => {
    try {
      // Check for token in URL parameters (from login redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');
      
      if (urlToken) {
        // Store token from URL and remove it from URL
        manager.setToken(urlToken);
        manager.storeToken(urlToken);
        
        // Clean up URL by removing token parameter
        urlParams.delete('token');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
      }
      
      const token = manager.getToken() || urlToken;

      // Dev bypass: on localhost with no token, still allow through so you can use admin UI without login
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (!token && process.env.NODE_ENV === 'development' && isLocalhost) {
        const graphqlUri = envs.apiBaseUrl ? `${envs.apiBaseUrl}/graphql` : 'http://localhost:4000/graphql';
        const client = new ApolloClient({
          link: createHttpLink({ uri: graphqlUri }),
          cache: new InMemoryCache(),
          defaultOptions: { query: { errorPolicy: 'all' }, mutate: { errorPolicy: 'all' } },
        });
        setGraphqlClient(client);
        setIsValid(TokenExpiredEnum.NOTEXPIRED);
        return;
      }

      if (!token) {
        setIsValid(TokenExpiredEnum.EXPIRED);
        return;
      }

      // Token is already stored if it came from URL, otherwise store it
      if (!urlToken) {
        manager.setToken(token);
        manager.storeToken(token);
      }

      let adminToken;
      try {
        adminToken = jwt.decode(token) as Token;
        console.log('Decoded token:', adminToken);
        console.log('Token isAdmin:', adminToken?.isAdmin);
        console.log('Token role:', adminToken?.role);
      } catch (error) {
        console.error('Error decoding token:', error);
        setIsValid(TokenExpiredEnum.EXPIRED);
        return;
      }

      // Check if user is admin - allow if isAdmin is true OR role is 'admin'
      // Note: The new backend uses 'role' field, old tokens might use 'isAdmin'
      const isAdmin = adminToken?.isAdmin === true || adminToken?.role === 'admin';
      if (adminToken && !isAdmin) {
        console.warn('User is not an admin. Token details:', {
          isAdmin: adminToken.isAdmin,
          role: adminToken.role,
          userId: adminToken.userId,
          email: adminToken.email
        });
        // Don't block access - let the backend decide
        // setIsValid(TokenExpiredEnum.UNAUTHORIZED);
        // return;
      }

      if (!envs.apiBaseUrl) {
        console.error('API base URL is not configured');
        setIsValid(TokenExpiredEnum.EXPIRED);
        return;
      }

      // Use proxy in development to avoid CORS issues
      // In production, try to use HTTPS but fall back to HTTP if needed
      let graphqlUri: string;
      if (process.env.NODE_ENV === 'development') {
        graphqlUri = '/graphql'; // Use proxy in development
      } else {
        let apiUrl = envs.apiBaseUrl || '';
        // If page is HTTPS, try to use HTTPS for API (avoid mixed content)
        // But if backend doesn't support HTTPS, we may need to allow HTTP
        // by using a proxy or different approach
        if (window.location.protocol === 'https:' && apiUrl.startsWith('http://')) {
          // Try HTTPS first
          const httpsUrl = apiUrl.replace('http://', 'https://');
          console.warn('⚠️ TokenValidation: Page is HTTPS but API is HTTP. Trying HTTPS:', httpsUrl);
          console.warn('⚠️ If backend doesn\'t support HTTPS, you may see errors.');
          console.warn('⚠️ Consider: 1) Enable HTTPS on backend, 2) Use CloudFront/CDN, or 3) Access API via different domain');
          apiUrl = httpsUrl;
        }
        graphqlUri = apiUrl + '/graphql';
      }
      
      // Create HTTP link
      const httpLink = createHttpLink({
        uri: graphqlUri,
      });

      // Create auth link that dynamically adds token to each request
      const authLink = setContext((_, { headers }) => {
        // Get token from manager (it might have been updated)
        const currentToken = manager.getToken();
        const authToken = currentToken && currentToken.startsWith('Bearer ') 
          ? currentToken 
          : currentToken 
            ? `Bearer ${currentToken}` 
            : '';
        
        // Debug logging
        console.log('🔐 Apollo Link: Adding auth header', {
          hasToken: !!currentToken,
          tokenLength: currentToken?.length,
          authHeader: authToken ? `${authToken.substring(0, 20)}...` : 'MISSING',
        });
        
        return {
          headers: {
            ...headers,
            authorization: authToken,
          },
        };
      });

      const client = new ApolloClient({
        link: from([authLink, httpLink]),
        cache: new InMemoryCache(),
        defaultOptions: {
          watchQuery: {
            errorPolicy: 'all',
          },
          query: {
            errorPolicy: 'all',
          },
        },
      });

      setGraphqlClient(client);
      setIsValid(TokenExpiredEnum.NOTEXPIRED);
    } catch (error) {
      console.error('TokenValidation error:', error);
      setIsValid(TokenExpiredEnum.EXPIRED);
    }
  }, [manager]);

  if (isValid === TokenExpiredEnum.CHECKING) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#02172a',
        color: '#fff'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (isValid === TokenExpiredEnum.EXPIRED) {
    // Avoid redirect loops: if we are already on login/unauthenticated/unauthorized,
    // don't bounce back to /unauthenticated with a recursive redirectUrl.
    const path = window.location.pathname;
    if (path.startsWith('/login') || path.startsWith('/unauthenticated') || path.startsWith('/unauthorized')) {
      return <Redirect to="/login" />;
    }
    return (
      <Redirect to={`/unauthenticated?redirectUrl=${window.location.href}`} />
    );
  }

  if (isValid === TokenExpiredEnum.UNAUTHORIZED) {
    return <Redirect to="/unauthorized" />;
  }

  if (!graphqlClient) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#02172a',
        color: '#fff'
      }}>
        <div>Initializing...</div>
      </div>
    );
  }

  return <ApolloProvider client={graphqlClient}>{children}</ApolloProvider>;
};

export default TokenValidation;
